import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@/ui';
import { useCopy } from '../locales/useDiscoverStrings';
import documentURL from './figure-viewer.html?url&no-inline';

export const figureLoadTimeout = 8000;
export function FigureFailure({ retry }: { retry(): void }) {
  const { c } = useCopy();
  return <div role="alert" className="dc-state"><p>{c("Couldn't open this figure.")}</p><Button variant="plain" onClick={retry}>{c('Try Again')}</Button></div>;
}
export function FigureDocument({ html, retry }: { html: string; retry(): void }) {
  const { c } = useCopy(), frame = useRef<HTMLIFrameElement>(null), channel = useRef<MessageChannel | undefined>(undefined), connected = useRef(false);
  const [nonce] = useState(() => crypto.randomUUID()), [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const disconnect = useCallback(() => { channel.current?.port1.close(); channel.current?.port2.close(); channel.current = undefined; }, []);
  const fail = useCallback(() => { disconnect(); setState('failed'); }, [disconnect]);
  useEffect(() => disconnect, [disconnect]);
  useEffect(() => { if (state !== 'loading') return; const timer = setTimeout(fail, figureLoadTimeout); return () => clearTimeout(timer); }, [state, fail]);
  const loaded = () => {
    // Never re-send private bytes after a child navigation, even if the iframe's src attribute is unchanged.
    if (connected.current || !frame.current?.contentWindow || typeof MessageChannel === 'undefined') { fail(); return; }
    connected.current = true;
    const target = frame.current.contentWindow, pipe = new MessageChannel(); channel.current = pipe; let sent = false;
    pipe.port1.onmessage = event => {
      const data: unknown = event.data;
      if (!data || typeof data !== 'object' || !('nonce' in data) || data.nonce !== nonce || !('type' in data)) return;
      if (data.type === 'ready' && !sent) { sent = true; pipe.port1.postMessage({ type: 'render', nonce, html }); }
      else if (data.type === 'rendered' && sent) { setState('ready'); }
      else if (data.type === 'failed') fail();
    };
    pipe.port1.onmessageerror = fail; pipe.port1.start();
    try { target.postMessage({ type: 'hylo-figure-connect', nonce }, '*', [pipe.port2]); } catch { fail(); }
  };
  if (state === 'failed') return <FigureFailure retry={retry} />;
  return <div className="dc-figure-document"><iframe ref={frame} title={c('Live figure')} sandbox="allow-scripts" referrerPolicy="no-referrer" src={`${documentURL}#${nonce}`} onLoad={loaded} onError={fail} style={{ visibility: state === 'ready' ? 'visible' : 'hidden' }} />{state === 'loading' && <div className="dc-figure-loading"><Spinner label={c('Loading figure')} /></div>}</div>;
}
